import { motion, AnimatePresence } from 'framer-motion'
import type { PlayingCard as PlayingCardType } from '@/types'
import { PlayingCard } from '@/components/common/PlayingCard'
import { calculateHandValue } from '../utils/baccaratLogic'
import type { DealingTarget } from '../hooks/useBaccaratGame'
import { cn } from '@/lib/utils'

interface HandAreaProps {
  title: string
  hand: PlayingCardType[]
  isWinner?: boolean
}

function HandArea({ title, hand, isWinner }: HandAreaProps) {
  const value = hand.length > 0 ? calculateHandValue(hand) : null

  return (
    <div className="flex flex-col items-center p-4">
      <h3
        className={cn(
          'font-cinzel text-sm tracking-wide mb-4',
          isWinner ? 'text-casino-gold' : 'text-casino-gold/60'
        )}
      >
        {title}
      </h3>

      {/* Cards */}
      <div className="flex gap-2 justify-center min-h-[96px]">
        <AnimatePresence>
          {hand.map((card, index) => (
            <PlayingCard
              key={`${card.suit}${card.value}-${index}`}
              card={card}
              delay={index * 0.1}
              size="md"
            />
          ))}
        </AnimatePresence>
      </div>

      {/* Hand value */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: value !== null ? 1 : 0, y: 0 }}
        className={cn(
          'mt-4 px-6 py-2 rounded-full font-bold text-xl',
          'bg-casino-black/70 border-2',
          isWinner ? 'border-casino-gold text-casino-gold' : 'border-casino-gold/40 text-casino-gold/80'
        )}
      >
        {value ?? '-'}
      </motion.div>
    </div>
  )
}

interface BaccaratTableProps {
  playerHand: PlayingCardType[]
  bankerHand: PlayingCardType[]
  winner: 'player' | 'banker' | 'tie' | null
  dealingTo?: DealingTarget
}

export function BaccaratTable({ playerHand, bankerHand, winner, dealingTo }: BaccaratTableProps) {
  return (
    <div className="relative flex-1 flex flex-col">
      {/* Table surface */}
      <div
        className="flex-1 rounded-t-[200px] rounded-b-xl mx-4 my-4 p-6 relative overflow-hidden"
        style={{
          background: 'radial-gradient(ellipse at center, #6b1a1a 0%, #4a0e0e 50%, #2d0808 100%)',
          boxShadow: 'inset 0 0 100px rgba(0,0,0,0.4), 0 10px 30px rgba(0,0,0,0.5)',
          border: '8px solid #2a1810',
        }}
      >
        {/* Felt texture */}
        <div
          className="absolute inset-0 pointer-events-none opacity-10"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          }}
        />

        {/* Decorative gold border */}
        <div className="absolute top-4 left-4 right-4 bottom-4 border border-casino-gold/20 rounded-t-[180px] rounded-b-lg pointer-events-none" />

        {/* Table title - más abajo para no chocar con el mazo */}
        <div className="absolute top-[45%] left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
          <span className="font-cinzel text-casino-gold/25 text-base tracking-[0.3em]">
            PUNTO Y BANCA
          </span>
        </div>

        {/* Divider */}
        <div className="absolute top-[55%] left-1/2 -translate-x-1/2 -translate-y-1/2 w-px h-32 bg-gradient-to-b from-transparent via-casino-gold/30 to-transparent" />

        {/* Card deck visual - CENTRO SUPERIOR con mano del dealer */}
        <div className="absolute top-4 left-1/2 -translate-x-1/2 flex items-center gap-3 z-10">
          {/* Deck stack effect */}
          <div className="relative">
            <div className="absolute top-1 left-1 w-12 h-[68px] rounded-lg bg-gray-800/60" />
            <div className="absolute top-0.5 left-0.5 w-12 h-[68px] rounded-lg bg-gray-700/80" />
            <motion.div
              animate={dealingTo ? { scale: [1, 0.97, 1] } : {}}
              transition={{ duration: 0.4 }}
              className="relative w-12 h-[68px] rounded-lg shadow-lg overflow-hidden border-2 border-casino-gold/60"
              style={{
                boxShadow: '2px 2px 0 #333, 4px 4px 0 #222, 6px 6px 8px rgba(0,0,0,0.5)',
              }}
            >
              <img
                src="/img/card-back.png"
                alt="Deck"
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
          {/* Dealer hand emoji - acompaña la carta hacia Punto o Banca */}
          <motion.div
            animate={dealingTo ? {
              y: [0, 120, 0],
              x: [0, dealingTo === 'player' ? -80 : 80, 0],
              rotate: [0, dealingTo === 'player' ? -30 : 30, 0],
              scale: [1, 1.15, 1]
            } : {}}
            transition={{
              duration: 0.8,
              ease: "easeInOut"
            }}
            className="text-3xl filter drop-shadow-lg"
            style={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}
          >
            🤚
          </motion.div>
        </div>

        {/* Dealing Card Animation - from center deck */}
        <AnimatePresence>
          {dealingTo && (
            <motion.div
              initial={{
                top: 16,
                left: '50%',
                x: '-50%',
                rotate: -5,
                scale: 0.8,
                opacity: 0,
              }}
              animate={{
                top: '50%',
                left: dealingTo === 'player' ? '25%' : '75%',
                x: '-50%',
                rotate: dealingTo === 'player' ? -5 : 5,
                scale: 1,
                opacity: 1,
              }}
              exit={{
                opacity: 0,
                scale: 0.8,
                transition: { duration: 0.2 }
              }}
              transition={{
                duration: 0.7,
                ease: [0.25, 0.1, 0.25, 1],
              }}
              className="absolute z-30 pointer-events-none"
              style={{ transformOrigin: 'center center' }}
            >
              <motion.div
                initial={{ rotateY: 0, rotateZ: -10 }}
                animate={{ rotateY: [0, 10, 0], rotateZ: [-10, 5, 0] }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="w-12 h-[68px] rounded-lg overflow-hidden shadow-2xl border-2 border-casino-gold/60"
                style={{
                  boxShadow: '0 10px 30px rgba(0,0,0,0.5), 0 5px 15px rgba(212,175,55,0.2)',
                }}
              >
                <img
                  src="/img/card-back.png"
                  alt="Card"
                  className="w-full h-full object-cover"
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Hands area */}
        <div className="flex justify-around items-center h-full pt-20">
          <HandArea
            title="JUGADOR (PUNTO)"
            hand={playerHand}
            isWinner={winner === 'player'}
          />
          <HandArea
            title="BANCA"
            hand={bankerHand}
            isWinner={winner === 'banker'}
          />
        </div>
      </div>
    </div>
  )
}
